import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import panelStyles, {CHANNEL_COLORS} from '../panelStyles';
import {formatMoney} from '../panelMetrics';
import anchorRect from '../tooltipPosition';

/**
 * Kanal performansi: adet + ciro + ADR yan yana.
 * Sadece adet gostermek yaniltici olurdu — cok rezervasyon getiren kanal
 * her zaman en cok parayi getirmiyor; sirali karsilastirma bunu gorunur kilar.
 *
 * Renkler kategorik sirada, asla dondurulmez; 6'dan fazla kanal olursa
 * kalanlar notr griye duser (uydurma renk uretmek yerine).
 */
export default function ChannelTable({channels}) {
    const {t} = useTranslation();
    const classes = panelStyles();
    // Kanal adi sutuna sigmiyorsa tam adi ipucunda goster.
    // Ipucu ekrana sabit konumlanir: kart overflow:hidden oldugu icin
    // satir icine yerlestirilen mutlak bir kutu kirpilirdi.
    const [tip, setTip] = useState(null);

    // ABV / ADR kisaltmalarinin acilimi baslikta durmuyor, ipucunda
    const showHint = (event, text) => {
        const box = anchorRect(event.currentTarget);
        setTip({x: box.centerX, y: box.top, limit: box.viewportWidth, name: text});
    };

    const showTip = (event, name) => {
        const el = event.currentTarget;
        // Yalnizca gercekten kirpilmissa: okunabilen adi tekrar yazmanin anlami yok
        if (el.scrollWidth <= el.clientWidth + 1) return;
        const box = anchorRect(el);
        setTip({x: box.centerX, y: box.top, limit: box.viewportWidth, name});
    };

    // Meta kanallar tabloda gosterilmez ama ciro ve adet toplamlarinda kalir.
    // Gizlenenlerin sayisi basligin altina yazilir: satirlarin toplami donem
    // cirosunu tutmayacagi icin farkin sebebi gorunur olmali.
    const visible = channels.filter((row) => !row.isMeta);
    const hidden = channels.filter((row) => row.isMeta);

    if (!visible.length) {
        return (
            <section className={classes.card}>
                <header className={classes.cardHead}>
                    <div>
                        <h2 className={classes.cardTitle}>{t('enw-panel-channels_title', 'Rezervasyon Kaynakları')}</h2>
                    </div>
                </header>
                <div className={classes.emptyBox}>
                    {/* Meta varken "rezervasyon yok" demek yanlis olur */}
                    {hidden.length > 0
                        ? t('enw-panel-channels_only_meta', 'Bu dönemde yalnızca meta kanallardan rezervasyon var; bu kanallar listelenmiyor.')
                        : t('enw-panel-channels_empty', 'Bu dönemde rezervasyon yok.')}
                </div>
            </section>
        );
    }

    // En yuksek ciroyu getiren kanal — adet siralamasindan farkli olabilir.
    // Gizli kanallar arasindan secilmemeli: tabloda olmayan bir adi yazmak olur.
    //
    // Kur olmadan farkli para birimleri karsilastirilamaz: 131.795 TRY ham sayi
    // olarak 87.182 EUR'dan buyuk ama degeri otuzda biri. Karsilastirma yalnizca
    // tesisin ana para birimindeki kanallar arasinda yapilir — ana para birimi,
    // en cok rezervasyonun yapildigi para birimi.
    const bookingsByCurrency = {};
    visible.forEach((row) => {
        const code = row.primaryRevenue.code;
        if (code) bookingsByCurrency[code] = (bookingsByCurrency[code] || 0) + row.count;
    });
    const mainCurrency = Object.entries(bookingsByCurrency)
        .sort((a, b) => b[1] - a[1])
        .map(([code]) => code)[0];

    const topRevenue = visible
        .filter((row) => row.primaryRevenue.code === mainCurrency)
        .reduce(
            (best, row) => (row.primaryRevenue.amount > (best ? best.primaryRevenue.amount : -1) ? row : best),
            null
        );

    return (
        <section className={classes.card}>
            <header className={classes.cardHead}>
                <div>
                    <h2 className={classes.cardTitle}>{t('enw-panel-channels_title', 'Rezervasyon Kaynakları')}</h2>
                    <p className={classes.cardDesc}>
                        {t('enw-panel-channels_desc', 'Adet, ciro ve gecelik fiyat')}
                        {hidden.length > 0 && ` · ${t('enw-panel-channels_meta_hidden', {
                            defaultValue: '{{count}} meta kanalı listelenmiyor, toplamlara dahil',
                            count: hidden.length,
                        })}`}
                    </p>
                </div>
                <span className={classes.cardNote}>
                    {t('enw-panel-channels_note', {
                        defaultValue: 'En çok adet {{count}} · En yüksek ciro {{revenue}}',
                        count: visible[0].name,
                        revenue: topRevenue ? topRevenue.name : '—',
                    })}
                </span>
            </header>

            <div className={classes.cardBody} style={{padding: '16px 0 4px'}}>
                <div className={classes.chanHead}>
                    <span>{t('enw-panel-channel', 'Kaynak')}</span>
                    <span />
                    <span style={{textAlign: 'right'}}>{t('enw-panel-count', 'Rezervasyon adedi')}</span>
                    <span style={{textAlign: 'right'}}>{t('enw-panel-revenue', 'Ciro')}</span>
                    <span style={{textAlign: 'right'}}>
                        <span
                            className={classes.hintAnchor}
                            onMouseEnter={(e) => showHint(e, t('enw-panel-abv_hint', 'Rezervasyon başına ortalama tutar (Ön rezervasyonlar hariç)'))}
                            onMouseLeave={() => setTip(null)}
                        >
                            ABV
                            <i className="fas fa-info-circle" />
                        </span>
                    </span>
                    <span style={{textAlign: 'right'}}>
                        <span
                            className={classes.hintAnchor}
                            onMouseEnter={(e) => showHint(e, t('enw-panel-adr_full_hint', 'Gecelik ortalama oda fiyatı'))}
                            onMouseLeave={() => setTip(null)}
                        >
                            ADR
                            <i className="fas fa-info-circle" />
                        </span>
                    </span>
                </div>

                {visible.map((row, index) => {
                    const color = CHANNEL_COLORS[index] || '#8a95a5';
                    const isTopRevenue = topRevenue && topRevenue.name === row.name;
                    return (
                        <div className={classes.chanRow} key={row.name}>
                            <span className={classes.chanName}>
                                <i className={classes.dot} style={{background: color}} />
                                <span
                                    className={classes.chanNameText}
                                    onMouseEnter={(e) => showTip(e, row.name)}
                                    onMouseLeave={() => setTip(null)}
                                >
                                    {row.name}
                                </span>
                            </span>

                            <span className={classes.track}>
                                <i className={classes.trackFill} style={{width: `${row.barWidth}%`, background: color}} />
                            </span>

                            <span className={`${classes.numCell} ${classes.chanCount}`}>
                                {row.count}
                                <small>%{Math.round(row.share)}</small>
                            </span>

                            <span
                                className={`${classes.numCell} ${classes.chanRevenue}`}
                                style={isTopRevenue ? {color: '#157f4f', fontWeight: 600} : undefined}
                            >
                                {row.primaryRevenue.code
                                    ? `${formatMoney(row.primaryRevenue.amount)} ${row.primaryRevenue.code}`
                                    : '—'}
                            </span>

                            <span className={`${classes.numCell} ${classes.chanAbv}`}>
                                {row.abv !== null ? `${formatMoney(row.abv)} ${row.primaryRevenue.code || ''}` : '—'}
                            </span>

                            <span className={`${classes.numCell} ${classes.chanAdr}`}>
                                {row.adr !== null ? `${formatMoney(row.adr)} ${row.primaryRevenue.code || ''}` : '—'}
                            </span>
                        </div>
                    );
                })}
            </div>

            {tip && (
                <div
                    className={classes.chanTip}
                    style={{
                        left: Math.min(Math.max(tip.x, 120), tip.limit - 120),
                        top: tip.y - 8,
                    }}
                >
                    {tip.name}
                </div>
            )}
        </section>
    );
}
